import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Users, Building2, ArrowLeft, Zap, TrendingUp, Calendar, RefreshCw } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import analytics from '@/utils/analytics';

const API = window.location.origin + '/api';

// Format subscriber count
const formatSubscribers = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Calculate time ago
const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return `${Math.floor(diffDays / 7)} weeks ago`;
};

// Check if item is very recent (within 48 hours)
const isVeryRecent = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  return diffHours <= 48;
};

const NewThisWeek = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    loadData();
    analytics.pageView('/new-this-week');
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const timestamp = Date.now();
      const res = await axios.get(`${API}/data/whats-new?brands_limit=20&creators_limit=20&_t=${timestamp}`);
      setData(res.data);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to load new data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandClick = (brand) => {
    analytics.brandClick(brand.brand_name, brand.detected_brand_id, 'new_this_week');
    navigate(`/brand/${encodeURIComponent(brand.brand_name)}`);
  };

  const handleCreatorClick = (creator) => {
    analytics.creatorClick(creator.channel_name, creator.creator_id, 'new_this_week');
    navigate(`/creator/${creator.creator_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading fresh discoveries...</p>
        </div>
      </div>
    );
  }

  const totalCount = data?.counts?.total || 0;
  const brandsCount = data?.counts?.brands || 0;
  const creatorsCount = data?.counts?.creators || 0;
  const brands = data?.brands || [];
  const creators = data?.creators || [];
  const hasNoData = totalCount === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="What's New - Fresh Sponsorship Data"
        description="Discover newly detected brands and creators. See the latest sponsorship relationships added to Sponsiq this week."
        path="/new-this-week"
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">
                Discover Brands
              </Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">
                Discover Creators
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Sponsorships
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="page-title">
                    What's New
                  </h1>
                  <p className="text-slate-600">Fresh sponsorship data from the last 7 days</p>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadData}
              className="text-slate-600"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>

          {/* Value Message */}
          {!hasNoData && (
            <div className="mt-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl" data-testid="value-message">
              <p className="text-emerald-800 font-medium flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                <span className="text-lg">{totalCount} new sponsors discovered this week</span>
              </p>
              <p className="text-emerald-600 text-sm mt-1">
                {brandsCount} brands and {creatorsCount} creators added to our database
              </p>
            </div>
          )}

          {lastRefreshed && (
            <p className="text-sm text-slate-500 mt-4 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Last updated {timeAgo(lastRefreshed.toISOString())}
            </p>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {hasNoData ? (
          /* Empty State */
          <Card className="p-12 text-center bg-white">
            <Zap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No new sponsors this week</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Our discovery engine runs every 12 hours. Check back soon for fresh data!
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/explore-brands">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Browse All Brands
                </Button>
              </Link>
              <Link to="/brands/discover">
                <Button variant="outline">
                  Browse All Creators
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="space-y-12">
            {/* New Brands Section */}
            {brands.length > 0 && (
              <section data-testid="new-brands-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      New Brands
                    </h2>
                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                      {brandsCount}
                    </Badge>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">
                  Brands recently detected sponsoring YouTube creators
                </p>

                <div className="grid gap-3">
                  {brands.map((brand, index) => {
                    const veryRecent = isVeryRecent(brand.first_detected_at);
                    
                    return (
                      <Card 
                        key={brand.detected_brand_id || index}
                        className={`p-4 hover:shadow-md transition-all cursor-pointer ${veryRecent ? 'ring-1 ring-emerald-200 bg-emerald-50/30' : ''}`}
                        onClick={() => handleBrandClick(brand)}
                        data-testid={`brand-card-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">
                                  {brand.brand_name}
                                </h3>
                                {veryRecent ? (
                                  <Badge className="bg-emerald-500 text-white text-xs">
                                    <Zap className="w-3 h-3 mr-0.5" />
                                    Just Added
                                  </Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                {brand.niche_category && (
                                  <span>{brand.niche_category}</span>
                                )}
                                <span>•</span>
                                <span>{timeAgo(brand.first_detected_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                              {brand.mention_count || 0}
                            </p>
                            <p className="text-xs text-slate-500">mentions</p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {brandsCount > 20 && (
                  <div className="mt-4 text-center">
                    <Link to="/explore-brands">
                      <Button variant="outline" size="sm">
                        View All {brandsCount} Brands
                      </Button>
                    </Link>
                  </div>
                )}
              </section>
            )}

            {/* New Creators Section */}
            {creators.length > 0 && (
              <section data-testid="new-creators-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      New Creators
                    </h2>
                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                      {creatorsCount}
                    </Badge>
                  </div>
                </div>
                <p className="text-slate-600 mb-6">
                  Creators recently added to our sponsorship database
                </p>

                <div className="grid gap-3">
                  {creators.map((creator, index) => {
                    const veryRecent = isVeryRecent(creator.first_detected_at);
                    
                    return (
                      <Card 
                        key={creator.creator_id || index}
                        className={`p-4 hover:shadow-md transition-all cursor-pointer ${veryRecent ? 'ring-1 ring-emerald-200 bg-emerald-50/30' : ''}`}
                        onClick={() => handleCreatorClick(creator)}
                        data-testid={`creator-card-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                              {creator.channel_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">
                                  {creator.channel_name}
                                </h3>
                                {veryRecent ? (
                                  <Badge className="bg-emerald-500 text-white text-xs">
                                    <Zap className="w-3 h-3 mr-0.5" />
                                    Just Added
                                  </Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                    New
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                {creator.niche && (
                                  <>
                                    <span>{creator.niche}</span>
                                    <span>•</span>
                                  </>
                                )}
                                <span>{timeAgo(creator.first_detected_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-600" style={{fontFamily: 'JetBrains Mono'}}>
                              {formatSubscribers(creator.subscriber_count)}
                            </p>
                            <p className="text-xs text-slate-500">subscribers</p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {creatorsCount > 20 && (
                  <div className="mt-4 text-center">
                    <Link to="/brands/discover">
                      <Button variant="outline" size="sm">
                        View All {creatorsCount} Creators
                      </Button>
                    </Link>
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        {/* Bottom CTA */}
        {!hasNoData && (
          <section className="mt-12 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>
              Discover Sponsorship Opportunities
            </h3>
            <p className="text-indigo-100 mb-6">
              Our discovery engine analyzes YouTube channels every 12 hours to find new brand partnerships
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/explore-brands">
                <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6">
                  Browse All Brands
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6">
                  Analyze Your Channel
                </Button>
              </Link>
            </div>
          </section>
        )}
      </main>

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default NewThisWeek;
